"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useState, type ReactNode } from "react";
import UserPageSetUpProfileHeader from "./UserPageSetUpProfileHeader";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { markIdentityGettingStartedSession } from "@/components/user/identity/getting-started/identityGettingStartedSession";
import UserSettingsClassification from "@/components/user/settings/UserSettingsClassification";
import UserSettingsPrimaryWallet from "@/components/user/settings/UserSettingsPrimaryWallet";
import UserSettingsSave from "@/components/user/settings/UserSettingsSave";
import UserSettingsUsername from "@/components/user/settings/UserSettingsUsername";
import type { ApiCreateOrUpdateProfileRequest } from "@/entities/IProfile";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import { ApiProfileClassification } from "@/generated/models/ApiProfileClassification";
import { commonApiPost } from "@/services/api/common-api";
import { useMutation } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
function FieldHelper(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-font-medium tw-leading-5 tw-text-iron-500">{children}</p>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const getInitialClassification = (profile: ApiIdentity): ApiProfileClassification => (profile as {
  readonly classification?: ApiProfileClassification | null | undefined;
}).classification ?? ApiProfileClassification.Pseudonym;
export default function UserPageSetUpProfile(t0) {
  const $ = _c(56);
  const {
    profile
  } = t0;
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  const {
    onProfileEdit
  } = useContext(ReactQueryWrapperContext);
  const router = useRouter();
  const pathname = usePathname();
  const [userName, setUserName] = useState(profile.handle ?? "");
  let t1;
  if ($[0] !== profile) {
    t1 = getInitialClassification(profile);
    $[0] = profile;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [classification, setClassification] = useState(t1);
  let t2;
  if ($[2] !== profile.wallets) {
    t2 = () => {
      const tdhWallets = (profile.wallets ?? []).toSorted(_temp);
      const topWallet = tdhWallets[0];
      return topWallet === undefined ? "" : topWallet.wallet;
    };
    $[2] = profile.wallets;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getHighestTdhWalletOrNone = t2;
  let t3;
  if ($[4] !== getHighestTdhWalletOrNone || $[5] !== profile.primary_wallet) {
    t3 = profile.primary_wallet && profile.primary_wallet.length > 0 ? profile.primary_wallet : getHighestTdhWalletOrNone();
    $[4] = getHighestTdhWalletOrNone;
    $[5] = profile.primary_wallet;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [primaryWallet, setPrimaryWallet] = useState(t3);
  const haveConsolidations = (profile.wallets?.length ?? 0) > 1;
  let t4;
  if ($[7] !== classification || $[8] !== primaryWallet || $[9] !== profile.classification || $[10] !== profile.handle || $[11] !== profile.primary_wallet || $[12] !== userName) {
    t4 = userName.toLowerCase() !== (profile.handle ?? "").toLowerCase() || primaryWallet !== profile.primary_wallet || classification !== profile.classification;
    $[7] = classification;
    $[8] = primaryWallet;
    $[9] = profile.classification;
    $[10] = profile.handle;
    $[11] = profile.primary_wallet;
    $[12] = userName;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const haveChanges = t4;
  const [mutating, setMutating] = useState(false);
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = async body => {
      setMutating(true);
      return await commonApiPost({
        endpoint: "profiles",
        body
      });
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== onProfileEdit || $[16] !== pathname || $[17] !== profile.handle || $[18] !== router || $[19] !== setToast) {
    t6 = updatedProfile => {
      if (!profile.handle?.trim() && updatedProfile.handle?.trim()) {
        markIdentityGettingStartedSession(updatedProfile.handle);
      }
      setToast({
        message: "Profile updated.",
        type: "success"
      });
      const newPath = (() => {
        const updatedHandle = (updatedProfile.handle ?? "").toLowerCase();
        const parts = pathname.split("/");
        if (parts.length > 1) {
          parts[1] = updatedHandle;
          return parts.join("/");
        }
        return `/${updatedHandle}`;
      })();
      router.replace(newPath, {
        scroll: false
      });
      onProfileEdit({
        profile: updatedProfile,
        previousProfile: null
      });
    };
    $[15] = onProfileEdit;
    $[16] = pathname;
    $[17] = profile.handle;
    $[18] = router;
    $[19] = setToast;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== setToast) {
    t7 = error => {
      setToast({
        message: error as string,
        type: "error"
      });
    };
    $[21] = setToast;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setMutating(false);
    };
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t6 || $[25] !== t7) {
    t9 = {
      mutationFn: t5,
      onSuccess: t6,
      onError: t7,
      onSettled: t8
    };
    $[24] = t6;
    $[25] = t7;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const updateUser = useMutation(t9);
  let t10;
  if ($[27] !== classification || $[28] !== profile.pfp || $[29] !== requestAuth || $[30] !== setToast || $[31] !== updateUser || $[32] !== userName) {
    t10 = async e => {
      e.preventDefault();
      const {
        success
      } = await requestAuth();
      if (!success) {
        setToast({
          message: "You must be logged in to save settings",
          type: "error"
        });
        return;
      }
      const body_0 = {
        handle: userName,
        classification
      };
      if (profile.pfp) {
        body_0.pfp_url = profile.pfp;
      }
      await updateUser.mutateAsync(body_0);
    };
    $[27] = classification;
    $[28] = profile.pfp;
    $[29] = requestAuth;
    $[30] = setToast;
    $[31] = updateUser;
    $[32] = userName;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const onSubmit = t10;
  const [userNameAvailable, setUserNameAvailable] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <UserPageSetUpProfileHeader />;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const t12 = profile.handle ?? "";
  let t13;
  if ($[35] !== t12 || $[36] !== userName) {
    t13 = <UserSettingsUsername userName={userName} originalUsername={t12} setUserName={setUserName} setIsAvailable={setUserNameAvailable} setIsLoading={setCheckingUsername} />;
    $[35] = t12;
    $[36] = userName;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== classification) {
    t14 = <UserSettingsClassification selected={classification} onSelect={setClassification} />;
    $[38] = classification;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <FieldHelper>You can change this later.</FieldHelper>;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t14) {
    t16 = <div className="tw-mt-6">{t14}{t15}</div>;
    $[41] = t14;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== haveConsolidations || $[44] !== primaryWallet || $[45] !== profile.wallets) {
    t17 = haveConsolidations && <div className="tw-mt-6"><UserSettingsPrimaryWallet wallets={profile.wallets ?? []} selected={primaryWallet} onSelect={setPrimaryWallet} /><FieldHelper>This wallet is shown as your main profile wallet and can be changed later.</FieldHelper></div>;
    $[43] = haveConsolidations;
    $[44] = primaryWallet;
    $[45] = profile.wallets;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const t18 = !haveChanges || !userNameAvailable || checkingUsername;
  let t19;
  if ($[47] !== mutating || $[48] !== t18) {
    t19 = <div className="tw-mt-7"><UserSettingsSave loading={mutating} disabled={t18} /></div>;
    $[47] = mutating;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== onSubmit || $[51] !== t13 || $[52] !== t16 || $[53] !== t17 || $[54] !== t19) {
    t20 = <div className="tailwind-scope"><section className="tw-mx-auto tw-w-full tw-max-w-xl"><div className="tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#08090b] tw-p-5 tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] sm:tw-p-6 lg:tw-p-7">{t11}<form onSubmit={onSubmit} className="tw-mt-6 tw-flex tw-flex-col">{t13}{t16}{t17}{t19}</form></div></section></div>;
    $[50] = onSubmit;
    $[51] = t13;
    $[52] = t16;
    $[53] = t17;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
}
function _temp(a, b) {
  return b.tdh - a.tdh;
}